import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Cake, WarningCircle } from "@phosphor-icons/react";

/** Today's and upcoming birthdays for authorized staff, each linking to the child's profile. */
export const BirthdayPanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/students/birthdays").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <div className="bg-white rounded-3xl border border-border p-6" data-testid="birthday-panel">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-2xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
          <Cake size={22} weight="duotone" />
        </span>
        <div>
          <h2 className="font-display text-xl font-semibold">Birthdays</h2>
          <p className="text-xs font-semibold text-muted-foreground">
            Today and the next {data.window_days} days
          </p>
        </div>
      </div>

      {data.today.length > 0 && (
        <div className="mt-5 rounded-2xl border-2 border-[#FBBF24] bg-amber-50 p-4" data-testid="birthdays-today">
          <p className="text-sm font-bold">Celebrating today</p>
          <ul className="mt-2 space-y-1">
            {data.today.map((b) => (
              <li key={b.id}>
                <Link to={`/admin/students/${b.id}`} className="text-sm font-bold text-[#3B82F6]"
                      data-testid={`birthday-today-${b.id}`}>
                  {b.name} turns {b.turning}
                </Link>
                {b.room ? <span className="text-xs text-muted-foreground"> · {b.room}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="mt-5 space-y-2" data-testid="birthdays-upcoming">
        {data.upcoming.filter((b) => !b.is_today).length === 0 && (
          <li className="text-sm text-muted-foreground">No birthdays in the next {data.window_days} days.</li>
        )}
        {data.upcoming.filter((b) => !b.is_today).slice(0, 8).map((b) => (
          <li key={b.id} className="flex items-center justify-between gap-3">
            <Link to={`/admin/students/${b.id}`} className="text-sm font-bold text-[#3B82F6] truncate"
                  data-testid={`birthday-${b.id}`}>
              {b.name}
            </Link>
            <span className="text-xs text-muted-foreground shrink-0">
              {new Date(`${b.date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {" · turns "}{b.turning}
            </span>
          </li>
        ))}
      </ul>

      {data.missing_birthdate.length > 0 && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/70 p-4"
             data-testid="birthdays-missing">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <WarningCircle size={16} weight="bold" />
            {data.missing_birthdate.length} profile{data.missing_birthdate.length === 1 ? "" : "s"} incomplete — no birthdate
          </p>
          <ul className="mt-2 space-y-1">
            {data.missing_birthdate.map((m) => (
              <li key={m.id}>
                <Link to={`/admin/students/${m.id}`} className="text-sm font-bold text-[#3B82F6]"
                      data-testid={`birthday-missing-${m.id}`}>
                  {m.name}
                </Link>
                <span className="text-xs text-muted-foreground"> · {m.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
